
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import VisitorsTrustEntityCodesEntity from 'Models/Security/Acl/VisitorsTrustEntityCodesEntity';
import UserTrustEntityCodesEntity from 'Models/Security/Acl/UserTrustEntityCodesEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';

import { gql } from '@apollo/client';
import { store } from 'Models/Store';
import { computed, runInAction } from 'mobx';

export interface ITrustEntityCodesEntityAttributes extends IModelAttributes {
	entityCode: string;


}

@entity('TrustEntityCodesEntity', 'Trust Entity Codes')
export default class TrustEntityCodesEntity extends Model
	implements ITrustEntityCodesEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsTrustEntityCodesEntity(),
		new UserTrustEntityCodesEntity(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	
	/**
	 * The entity code
	 */
	@Validators.Required()
	@Validators.Unique()
	@observable
	@attribute<TrustEntityCodesEntity, string>()
	@CRUD({
		name: 'Entity Code',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public entityCode: string;


	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<ITrustEntityCodesEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<ITrustEntityCodesEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.entityCode !== undefined) {
				this.entityCode = attributes.entityCode;
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}

	// We need trustee codes in a lot of places throughout the app, and it's unlikely to change between requests,
	// so we use a static variable here to cache the value to prevent fetching it more than once
	@observable private static _trusteeCodes = [] as string[];
	@observable private static _promise = null as Promise<string[]> | null;
	public static trusteeCodes(): Promise<string[]> {
		if (TrustEntityCodesEntity._promise) return TrustEntityCodesEntity._promise;

		TrustEntityCodesEntity._promise = new Promise(resolve => {
			store.apolloClient.query({
				query: gql`
					query {
						trustEntityCodesEntitys {
							id
							entityCode
						}
					}
				`,
				fetchPolicy: 'no-cache',
			}).then(({ data }) => {
				runInAction(() => {
					TrustEntityCodesEntity._trusteeCodes = data.trustEntityCodesEntitys.map((code: { entityCode: string }) => code.entityCode);
					resolve(TrustEntityCodesEntity._trusteeCodes);
				});
			});
		});

		return TrustEntityCodesEntity._promise;
	}

	// If we want to get the trustee codes, but can't wait, then this will return the current value
	// It will still fetch the trustee codes, but you won't see the results until a future call
	// If called from within an observer, though, it'll still rerender because this is a computed field
	@computed
	public static get trusteeCodesSync() {
		TrustEntityCodesEntity.trusteeCodes();
		return TrustEntityCodesEntity._trusteeCodes;
	}

}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(TrustEntityCodesEntity.prototype, 'created');
CRUD(modifiedAttr)(TrustEntityCodesEntity.prototype, 'modified');
